import classNames from 'classnames';
import SVGIcon from '../../../components/atoms/icons';
import Text from '../../../components/atoms/text';
import MaintenanceService, {
	IMaintenance,
} from '../../../services/Maintenance.service';
import { dateObjToStr } from '../../../utils/date';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReportsService, {
	ContainerReportProps,
	MaintenanceReport,
} from '../../../services/Reports.service';
import Label from '../../../components/atoms/text/label';
import Button from '../../../components/atoms/button';
import Table, { TableColumn } from '../../../components/organisms/table';

const MaintenanceLayout = () => {
	const [searchParams] = useSearchParams();
	const [reportData, setReportData] = useState<MaintenanceReport[]>([]);
	const [loading, setLoading] = useState(false);
	const [isPrinting, setIsPrinting] = useState(false);
	const printableRef = useRef<HTMLDivElement>(null);
	const paginaRef = useRef<HTMLDivElement>(null);
	const { t } = useTranslation();
	const [date, setDate] = useState('');

	useEffect(() => {
		const pagina = paginaRef.current;
		if (!pagina) return;
		let larguraAnterior = 0;
		let quadro = 0;

		const observador = new ResizeObserver(([entrada]) => {
			const larguraDisponivel = entrada.contentRect.width;
			if (!larguraDisponivel || larguraDisponivel === larguraAnterior) return;
			larguraAnterior = larguraDisponivel;
			cancelAnimationFrame(quadro);
			// Aplica o zoom fora da notificação para não gerar um ciclo de resize.
			quadro = requestAnimationFrame(() => {
				// Mantém a mesma largura e escala do relatório de consultas.
				const larguraRelatorio = Math.max(1028, larguraDisponivel);
				pagina.style.setProperty('--largura-relatorio', `${larguraRelatorio}px`);
				pagina.style.setProperty('--escala-relatorio', `${larguraDisponivel / larguraRelatorio}`);
			});
		});
		observador.observe(pagina);
		return () => {
			observador.disconnect();
			cancelAnimationFrame(quadro);
		};
	}, []);

	useEffect(() => {
		const loadData = async () => {
			setLoading(true);
			const params = {
				dateBegin: searchParams.get('dateBegin') || '',
				dateEnd: searchParams.get('dateEnd') || '',
				accountId: searchParams.get('accountId') || '',
				yard: searchParams.get('yard') || '',
			};
			if (params.dateBegin && params.dateEnd) {
				setDate(
					`${dateObjToStr(params.dateBegin)} - ${dateObjToStr(params.dateEnd)}`
				);
			}
			const reportsData = await ReportsService.getAllMaintenances({
				accountId: params.accountId,
				yard: params.yard,
				dateBegin: new Date(params.dateBegin),
				dateEnd: new Date(params.dateEnd),
			});
			if (!reportsData) return;
			setReportData(reportsData);
			setLoading(false);
		};

		loadData();
	}, [searchParams, t]);

	const handlePrint = () => {
		setIsPrinting(true);

		// Pequeno delay para garantir que o CSS de impressão seja aplicado
		setTimeout(() => {
			window.print();
		}, 100);
	};

	// Limpa o estado de impressão após o print
	useEffect(() => {
		const afterPrint = () => {
			setIsPrinting(false);
		};

		window.addEventListener('afterprint', afterPrint);
		return () => {
			window.removeEventListener('afterprint', afterPrint);
		};
	}, []);

	// Soma apenas totalLabor
	const calcTotalLabor = () => {
		const result = reportData.reduce(
			(acc, maintenance) => acc + (maintenance.totalLabor || 0),
			0
		);
		return result.toFixed(2);
	};

	// Soma apenas totalMat
	const calcTotalMat = () => {
		const result = reportData.reduce(
			(acc, maintenance) => acc + (maintenance.totalMat || 0),
			0
		);
		return result.toFixed(2);
	};

	const calcTotal = () => {
		const result = Number(calcTotalMat()) + Number(calcTotalLabor());
		return result.toFixed(2);
	};

	const handleMaterials = (data: MaintenanceReport) =>
		data.materials.map((material, index) => (
			<div
				className="grid grid-cols-4 px-2 py-1"
				key={`material-${data.id}-${material.id}`}
			>
				<div className="flex flex-col gap-1">
					<Label className="text-sm">{t('misc.item')}</Label>
					<Text>{index + 1}</Text>
				</div>
				<div className="flex flex-col gap-1">
					<Label className="text-sm">{t('misc.description')}</Label>
					<Text>{material.name}</Text>
				</div>
				<div className="flex flex-col gap-1">
					<Label className="text-sm">{t('misc.quantity')}</Label>
					<Text>{material.quantity}</Text>
				</div>
				<div className="flex flex-col gap-1">
					<Label className="text-sm">{t('misc.price')}</Label>
					<Text>{material.price}</Text>
				</div>
			</div>
		));

	const columns: TableColumn<MaintenanceReport>[] = [
		{
			key: 'date',
			label: t('date.title'),
			children: (row) => (
				<Text className="text-xs">{dateObjToStr(row.date)}</Text>
			),
		},
		{ key: 'container', label: t('port_management.container') },
		{
			key: 'materials',
			label: t('misc.price'),
			children: (row) => (
				<Text className="text-xs">
					R$ {(row.totalLabor + row.totalMat).toFixed(2)}
				</Text>
			),
		},
	];

	return (
		<div ref={paginaRef}>
			<div className="relatorio-conteudo flex flex-col gap-8 p-16 print:p-0">
				<Button
					className="self-end w-fit print:hidden"
					onClick={handlePrint}
					disabled={loading || reportData.length === 0}
				>
					{t('financial.exportPDF')}
				</Button>
				<div className="flex justify-end">
					<Text className="font-semibold text-sm">{date}</Text>
				</div>
				<div className="flex flex-col items-center">
					<SVGIcon
						icon="InovaReefer"
						className="w-36 h-auto justify-self-center"
					/>
					<Text className="text-primary text-sm font-semibold text-center block">
						CNPJ: 57.456.341/0001-93
					</Text>
				</div>
				<Text as="h2" className="text-center block">
					Relatório de Manutenção
				</Text>
				<div className="overflow-x-auto w-full">
					<Table
						data={reportData}
						columns={columns}
						className={classNames('grid-cols-[1fr_1fr_1fr]')}
					/>
				</div>
				<Text as="h3" className="text-primary text-center">
					{t('misc.total_price')}: R$ {calcTotal()}
				</Text>
				{reportData.map((data) => (
					<div className="flex flex-col border border-gray">
						<div className="grid grid-cols-3 border border-gray">
							<Text className="border border-gray px-2 py-1">
								ESTIMATE
							</Text>
							<Text className="border border-gray px-2 py-1">
								EST Nº {data.id}
							</Text>
							<Text className="border border-gray px-2 py-1">
								{t('date.title')}: {dateObjToStr(data.date)}
							</Text>
						</div>
						<div className="grid grid-cols-2 border border-gray">
							<div className="flex flex-col gap-1 border border-gray px-2 py-1">
								<Label className="text-sm">
									{t('misc.account')}
								</Label>
								<Text>{data.yard.harbor?.client?.fantasyName}</Text>
							</div>
							<div className="flex flex-col gap-1 border border-gray px-2 py-1">
								<Label className="text-sm">
									{t('misc.terminal')}
								</Label>
								<Text>{data.yard.name}</Text>
							</div>
						</div>
						<div className="grid grid-cols-4 border border-gray">
							<div className="flex flex-col gap-1 border border-gray px-2 py-1">
								<Label className="text-sm">
									{t('port_management.container')}
								</Label>
								<Text>{data.container}</Text>
							</div>
							<div className="flex flex-col gap-1 border border-gray px-2 py-1">
								<Label className="text-sm">
									{t('misc.marks_equipment')}
								</Label>
							</div>
							<div className="flex flex-col gap-1 border border-gray px-2 py-1">
								<Label className="text-sm">
									{t('maintenance.voltage')}
								</Label>
								<Text>{data.voltage}</Text>
							</div>
							<div className="flex flex-col gap-1 border border-gray px-2 py-1">
								<Label className="text-sm">{t('misc.size')}</Label>
								<Text>{data.size}</Text>
							</div>
						</div>
						<div className="flex flex-col gap-1 border border-gray">
							{handleMaterials(data)}
						</div>

						<div className="grid grid-cols-2 border border-gray">
							<div className="flex flex-col gap-1 border border-gray px-2 py-1">
								<Label className="text-sm">Total Mat.</Label>
								<Text>R$ {data.totalMat.toFixed(2)}</Text>
							</div>
							<div className="flex flex-col gap-1 border border-gray px-2 py-1">
								<Label className="text-sm">Total Labor</Label>
								<Text>R$ {data.totalLabor.toFixed(2)}</Text>
							</div>
						</div>
						<div className="grid border border-gray">
							<div className="flex flex-col gap-1 border border-gray px-2 py-1">
								<Label className="text-sm">
									Parts prices based on standard catalog
									procurement price lists subject to change
									without notice Procurement and delivery charges
									may be added for spacialservices or itens not
									available locally.
								</Label>
							</div>
						</div>
						<div className="grid border border-gray">
							<div className="flex flex-col gap-1 border border-gray px-2 py-1">
								<Label className="text-sm">
									{data.description}
								</Label>
							</div>
						</div>
						<div className="grid border border-gray">
							<div className="flex flex-col gap-1 border border-gray px-2 py-1">
								<Label className="text-sm">{t('misc.total')}</Label>
								<Text>
									R${' '}
									{(data.totalLabor + data.totalMat).toFixed(2)}
								</Text>
							</div>
						</div>
						<div className="grid border border-gray">
							<div className="flex flex-col gap-1 border border-gray px-2 py-1">
								<Label className="text-sm">
									{t('misc.evidencies')}
								</Label>
								{data.images?.map((src, index) => (
									<img
										className="w-96 h-auto"
										alt={`maintenance-${data.id}-${index}`}
										src={src}
									/>
								))}
							</div>
						</div>
					</div>
				))}
				<div className="mt-24 text-center">
					<img
						className="mx-auto"
						src="/signature.png"
						alt="assinatura"
					/>
					<p>Francisco de Paula Luca</p>
					<p>CREA-AM 13.225D</p>
					<p>Engenheiro Proprietário</p>
					<p>
						Escritório: Rua Professor Guilherme Nery nº 480 - Parque 10
						de novembro - CEP 69.055-350 - Manaus/AM
					</p>
				</div>
			</div>
		</div>
	);
};

export default MaintenanceLayout;
